import React, { useState, useRef, useEffect } from "react";
import { consoleApi } from "../../services/consoleApiService";
import {
  getApiErrorMessage,
  readFeedbackImageAsBase64,
  toFeedbackImageDataUrl,
  FEEDBACK_IMAGE_TYPES,
} from "../../utils/consoleUtils";
import { ErrorHandler } from "../../utils/errorHandler";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import "../../styles/console.css";

const MAX_IMAGES = 4;

function RobotIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="4" y="10" width="16" height="12" rx="2" />
      <circle cx="9" cy="15" r="1.5" fill="currentColor" />
      <circle cx="15" cy="15" r="1.5" fill="currentColor" />
      <path d="M9 10V8a2 2 0 012-2h2a2 2 0 012 2v2" />
      <path d="M12 4v2" />
      <path d="M8 4h2" />
      <path d="M14 4h2" />
    </svg>
  );
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachedImages, setAttachedImages] = useState([]);
  const [imageError, setImageError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open && messages.length) scrollToBottom();
  }, [open, messages]);

  const canSend = (input || "").trim() || attachedImages.length > 0;

  const handleImageSelect = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = "";
    if (!file || attachedImages.length >= MAX_IMAGES) return;
    setImageError(null);
    try {
      const { base64, contentType } = await readFeedbackImageAsBase64(file);
      setAttachedImages((prev) => [
        ...prev,
        { contentType, data: base64, previewUrl: toFeedbackImageDataUrl(base64, contentType) },
      ]);
    } catch (err) {
      ErrorHandler.logError("AssistantWidget - addImage", err);
      setImageError(err.message || getApiErrorMessage(err, "Could not add image."));
    }
  };

  const removeImage = (index) => {
    setAttachedImages((prev) => prev.filter((_, i) => i !== index));
    setImageError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSend || loading) return;
    const text = (input || "").trim();
    setError(null);
    setImageError(null);
    const userMessage = { role: "user", content: text || "(Image attached)" };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    const imagesToSend = attachedImages.map(({ contentType, data }) => ({ content_type: contentType, data }));
    setAttachedImages([]);
    setLoading(true);
    try {
      const history = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const res = await consoleApi.assistant.chat({
        messages: history,
        ...(imagesToSend.length ? { images: imagesToSend } : {}),
      });
      const assistantContent =
        res?.message?.content ?? "No response.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assistantContent },
      ]);
    } catch (err) {
      ErrorHandler.logError("AssistantWidget - chat", err);
      const msg = getApiErrorMessage(err, "Assistant request failed.");
      setError(msg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${msg}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="console-assistant-fab"
        onClick={() => setOpen(true)}
        aria-label="Open assistant"
      >
        <RobotIcon className="console-assistant-fab-icon" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="console-assistant-modal" onOpenChange={setOpen}>
          <DialogHeader>
            <DialogTitle className="console-assistant-title">
              Console Assistant
            </DialogTitle>
          </DialogHeader>
          <div className="console-assistant-body">
            <div className="console-assistant-messages">
              {messages.length === 0 && (
                <p className="console-assistant-placeholder">
                  Ask about the console, database, or how to do something. For example: &quot;How do I approve a submission?&quot; or &quot;How many clubs are there?&quot;
                </p>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`console-assistant-msg console-assistant-msg--${m.role}`}
                >
                  <div className="console-assistant-msg-content">{m.content}</div>
                </div>
              ))}
              {loading && (
                <div className="console-assistant-msg console-assistant-msg--assistant">
                  <div className="console-assistant-msg-content console-assistant-msg--thinking">
                    Thinking…
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            {error && (
              <p className="console-assistant-error" role="alert">
                {error}
              </p>
            )}
            {imageError && (
              <p className="console-assistant-error" role="alert">
                {imageError}
              </p>
            )}
            <form onSubmit={handleSubmit} className="console-assistant-form">
              {attachedImages.length > 0 && (
                <div className="console-assistant-images">
                  {attachedImages.map((img, i) => (
                    <div key={i} className="console-assistant-image-wrap">
                      <img
                        src={img.previewUrl}
                        alt=""
                        className="console-assistant-image-preview"
                      />
                      <button
                        type="button"
                        className="console-assistant-image-remove"
                        onClick={() => removeImage(i)}
                        aria-label="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="console-assistant-input-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FEEDBACK_IMAGE_TYPES.join(",")}
                  className="console-assistant-file-input"
                  onChange={handleImageSelect}
                  aria-hidden
                />
                <textarea
                  className="console-assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (canSend && !loading) handleSubmit(e);
                    }
                  }}
                  placeholder="Ask anything about the console or database…"
                  rows={2}
                  disabled={loading}
                  maxLength={4000}
                  aria-label="Message"
                />
                <button
                  type="button"
                  className="console-assistant-add-image"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || attachedImages.length >= MAX_IMAGES}
                  title={attachedImages.length >= MAX_IMAGES ? `Max ${MAX_IMAGES} images` : "Add image"}
                  aria-label="Add image"
                >
                  <Plus className="console-assistant-add-image-icon" size={20} />
                </button>
              </div>
              <Button
                type="submit"
                disabled={loading || !canSend}
                className="console-assistant-send"
              >
                Send
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
